/**
 * deposito_cliente.tsx — Modal para subir comprobante de depósito bancario
 *
 * Este componente maneja el flujo completo de pago por depósito:
 *
 *   PASO 1 — Seleccionar imagen
 *     El usuario toma una foto del comprobante con la cámara o la elige de la galería.
 *
 *   PASO 2 — Análisis con IA
 *     Se envía la imagen a N8N (REACT_APP_N8N_URL_DEPOSITO).
 *     N8N usa IA (GPT/Vision) para leer el comprobante y extraer:
 *       fecha, monto, número de comprobante, banco emisor, destinatario.
 *     También valida que la cuenta destino sea la de Sisnetel.
 *
 *   PASO 3 — Confirmar pago
 *     Si el comprobante es válido, el botón "Pagar ahora" llama a otro webhook
 *     (REACT_APP_N8N_URL_DEPOSITO_PAGAR) que registra el pago en el sistema.
 *
 *   PASO 4 — Comprobante
 *     Si el pago fue exitoso, se genera un comprobante visual (SVG desde N8N)
 *     que el usuario puede ver y compartir por WhatsApp u otras apps.
 *
 * Variables de entorno que usa:
 *   REACT_APP_N8N_URL_DEPOSITO          → Analizar imagen con IA
 *   REACT_APP_N8N_URL_DEPOSITO_PAGAR    → Registrar el pago
 *   REACT_APP_N8N_URL_DETALLE_FACTURA   → Obtener detalle de la factura para el comprobante
 *   REACT_APP_N8N_URL_COMPROBANTE       → Generar SVG del comprobante
 */

import React, { useState, useRef } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import ComprobanteTicket from "./ComprobanteTicket";

// Props que recibe este componente desde su componente padre (Pagos.tsx)
interface Props {
  show: boolean;           // true = el modal es visible
  onHide: () => void;      // función para cerrarlo
  clienteNombre: string;   // nombre del cliente actual
  clienteEstado: string;   // estado del cliente (ACTIVO, SUSPENDIDO, etc.)
  cedula: string;          // cédula del cliente para incluir en el pago
}

// Tiempo máximo de espera para cada llamada a N8N (en segundos)
const TIMEOUT_SEG = 10;

/**
 * Convierte una imagen en formato dataUrl (base64) a un objeto File.
 * Necesario porque la cámara de Capacitor devuelve dataUrl
 * pero la API de N8N espera un archivo (FormData).
 *
 * Ejemplo de dataUrl: "data:image/jpeg;base64,/9j/4AAQSkZJRg..."
 */
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/jpeg"; // Extrae el tipo MIME (ej: "image/jpeg")
  const binary = atob(data); // Decodifica de base64 a texto binario
  const array = new Uint8Array(binary.length); // Array de bytes
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  return new File([array], filename, { type: mime });
};

const DepositoClienteModal: React.FC<Props> = ({
  show, onHide, clienteNombre, clienteEstado, cedula,
}) => {
  // ── Estados del flujo ────────────────────────────────────────────────────────
  const [file, setFile] = useState<File | null>(null);           // Imagen del comprobante
  const [previewUrl, setPreviewUrl] = useState<string>("");      // URL para mostrar la imagen en pantalla
  const [isPdf, setIsPdf] = useState(false);                     // true si es PDF (no imagen)
  const [step, setStep] = useState<1 | 2>(1);                    // Paso actual: 1=seleccionar, 2=analizar
  const [loading, setLoading] = useState(false);                 // true mientras espera la API
  const [mensaje, setMensaje] = useState("");                    // Mensaje de éxito o error
  const [resultado, setResultado] = useState<any>(null);         // Respuesta del análisis de IA
  const [validado, setValidado] = useState(false);               // true si el comprobante es válido
  const [enviandoPago, setEnviandoPago] = useState(false);       // true mientras registra el pago
  const [pagoExitoso, setPagoExitoso] = useState(false);         // true cuando el pago se registró
  const [mensajeTipo, setMensajeTipo] = useState<"success" | "danger" | "warning" | "info">("info");
  const [errorPago, setErrorPago] = useState(false);
  const [detalleFactura, setDetalleFactura] = useState<any>(null); // Detalle de la factura pagada
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [comprobanteBase64, setComprobanteBase64] = useState<string>(''); // SVG del comprobante
  const [showComprobante, setShowComprobante] = useState(false);
  const [camError, setCamError] = useState("");                  // Error de cámara
  const [errorComprobante, setErrorComprobante] = useState("");  // Error al generar comprobante
  const [cuentaCopiada, setCuentaCopiada] = useState<string | null>(null); // banco cuya cuenta se acaba de copiar

  const copiarCuenta = (banco: string, cuenta: string) => {
    navigator.clipboard?.writeText(cuenta.replace(/\s/g, '')).then(() => {
      setCuentaCopiada(banco);
      setTimeout(() => setCuentaCopiada(null), 2000);
    });
  };

  // Cuentas regresivas (una para análisis, otra para generación del comprobante)
  const [countdown, setCountdown] = useState<number | null>(null);
  const [countdownComp, setCountdownComp] = useState<number | null>(null);

  // useRef guarda referencias a los intervalos sin causar re-render
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intervalCompRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Funciones de cuenta regresiva ────────────────────────────────────────────

  /** Inicia una cuenta regresiva desde TIMEOUT_SEG hasta 0, actualizando un estado cada segundo */
  const iniciarCuenta = (
    setter: React.Dispatch<React.SetStateAction<number | null>>,
    ref: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  ) => {
    let remaining = TIMEOUT_SEG;
    setter(remaining);
    ref.current = setInterval(() => {
      remaining -= 1;
      setter(remaining > 0 ? remaining : null); // Al llegar a 0, pone null
      if (remaining <= 0 && ref.current) clearInterval(ref.current);
    }, 1000);
  };

  /** Detiene una cuenta regresiva y limpia el estado */
  const detenerCuenta = (
    setter: React.Dispatch<React.SetStateAction<number | null>>,
    ref: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  ) => {
    if (ref.current) clearInterval(ref.current);
    setter(null);
  };

  /**
   * Hace una petición fetch con timeout.
   * Si tarda más de TIMEOUT_SEG segundos, lanza un error con mensaje "TIMEOUT".
   * Usamos AbortController que es la forma estándar de cancelar peticiones fetch.
   */
  const fetchConTimeout = async (url: string, options: RequestInit): Promise<Response> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_SEG * 1000);
    try {
      const res = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timer);
      return res;
    } catch (e: any) {
      clearTimeout(timer);
      if (e?.name === 'AbortError') throw new Error('TIMEOUT');
      throw e;
    }
  };

  /** Resetea todos los estados y cierra el modal */
  const handleClose = () => {
    setStep(1); setFile(null); setPreviewUrl(""); setIsPdf(false);
    setResultado(null); setMensaje(""); setMensajeTipo("info");
    setErrorPago(false); setValidado(false); setLoading(false);
    setEnviandoPago(false); setPagoExitoso(false); setCamError("");
    setDetalleFactura(null); setLoadingDetalle(false);
    setComprobanteBase64(''); setShowComprobante(false);
    setErrorComprobante('');
    detenerCuenta(setCountdown, intervalRef);
    detenerCuenta(setCountdownComp, intervalCompRef);
    onHide();
    window.location.reload(); // Recarga para actualizar el estado del cliente
  };

  // ── Captura de imagen ────────────────────────────────────────────────────────

  /** Abre la cámara del teléfono para tomar una foto del comprobante */
  const handleTakePhoto = async () => {
    setCamError("");
    try {
      const photo = await Camera.getPhoto({
        quality: 80,                            // Calidad del 80% (balance tamaño/calidad)
        resultType: CameraResultType.DataUrl,   // Devuelve la imagen como base64
        source: CameraSource.Camera,            // Usar cámara (no galería)
        correctOrientation: true,               // Corrige si la foto queda rotada
      });
      if (photo.dataUrl) {
        setFile(dataUrlToFile(photo.dataUrl, "foto_comprobante.jpg"));
        setPreviewUrl(photo.dataUrl);
        setIsPdf(false);
        setMensaje(""); setResultado(null); setValidado(false); setErrorPago(false);
      }
    } catch { setCamError("No se pudo acceder a la cámara."); }
  };

  /** Abre la galería del teléfono para seleccionar una imagen existente */
  const handleGallery = async () => {
    setCamError("");
    try {
      const photo = await Camera.getPhoto({
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Photos,            // Galería en vez de cámara
        correctOrientation: true,
      });
      if (photo.dataUrl) {
        setFile(dataUrlToFile(photo.dataUrl, "comprobante_galeria.jpg"));
        setPreviewUrl(photo.dataUrl);
        setIsPdf(false);
        setMensaje(""); setResultado(null); setValidado(false); setErrorPago(false);
      }
    } catch { setCamError("No se pudo abrir la galería."); }
  };

  /** Avanza al paso 2 y dispara el análisis de la imagen */
  const handleNext = () => { setStep(2); analizarComprobante(); };

  // ── Análisis con IA ──────────────────────────────────────────────────────────

  /**
   * Envía la imagen a N8N para que la analice con IA.
   * N8N usa GPT-4 Vision para leer el comprobante y validar los datos.
   */
  const analizarComprobante = async () => {
    if (!file) return;
    setLoading(true);
    setMensaje("");
    iniciarCuenta(setCountdown, intervalRef); // Inicia cuenta regresiva visual

    // FormData es el formato para enviar archivos por HTTP
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetchConTimeout(process.env.REACT_APP_N8N_URL_DEPOSITO!, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      setResultado(data);

      if (data.validacion_cuenta === "validado") {
        setValidado(true);
        setMensaje("✅ Comprobante válido. Puedes proceder con el pago.");
        setMensajeTipo("success");
        setErrorPago(false);
      } else {
        setValidado(false);
        setMensaje("❌ Comprobante no válido. Contacta a soporte.");
        setMensajeTipo("danger");
        setErrorPago(true);
      }
    } catch (e: any) {
      const esTimeout = e?.message === 'TIMEOUT';
      setMensaje(esTimeout
        ? "⏱️ El servidor tardó demasiado. Intenta de nuevo."
        : "❌ Error al procesar el comprobante. Intenta de nuevo.");
      setMensajeTipo("danger");
      setErrorPago(true);
    } finally {
      detenerCuenta(setCountdown, intervalRef);
      setLoading(false);
    }
  };

  // ── Generación del comprobante visual ────────────────────────────────────────

  /**
   * Después de un pago exitoso, genera el comprobante visual en SVG.
   * Proceso:
   *   1. Obtiene el detalle de la factura pagada (para saber el plan, fechas, etc.)
   *   2. Construye un objeto con todos los datos del comprobante
   *   3. Envía ese objeto a N8N que genera el SVG con logo y diseño de Sisnetel
   *
   * @param facturas_id - ID de la factura que se acaba de pagar
   * @param resData - Datos del depósito (banco, monto, número de comprobante)
   */
  const generarComprobante = async (facturas_id: string, resData: any) => {
    setLoadingDetalle(true);
    setErrorComprobante('');
    iniciarCuenta(setCountdownComp, intervalCompRef);

    try {
      // Paso 1: Obtener detalle de la factura
      const resDetalle = await fetchConTimeout(process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ facturas_id }),
      });
      const rawDetalle = await resDetalle.json();
      const detalle = Array.isArray(rawDetalle) ? rawDetalle[0] : rawDetalle;

      if (detalle?.estado === "exito") {
        setDetalleFactura(detalle);

        // Datos del usuario logueado (para el correo y celular en el comprobante)
        const clientData = (() => {
          try { return JSON.parse(localStorage.getItem('clientData') || '{}'); }
          catch { return {}; }
        })();

        const fac = detalle.factura;
        const items: any[] = detalle.items || [];
        // Busca el ítem con valor mayor a 0 (el ítem del plan)
        const item = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
        // La descripción puede tener varias líneas con info del plan
        const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);

        // Fecha y hora actual formateada para "Fecha de impresión"
        const now = new Date();
        const p2 = (n: number) => String(n).padStart(2, '0'); // Rellena con cero: 5 → "05"
        const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;

        // Convierte fecha "2025-01-15" → "15/01/2025"
        const fmtDate = (d: string) => {
          if (!d) return '';
          const pts = d.split('-');
          return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d;
        };

        // Paso 2: Construir el payload para el comprobante
        const payload = {
          clienteNombre,
          clienteCedula: cedula,
          clienteCelular: clientData?.movil || '',
          clienteCorreo: clientData?.correo || '',
          facturaId: String(fac.id).padStart(8, '0'),          // ID con ceros a la izquierda: "00001234"
          planNombre: lines[0] || '',
          facturacion: (lines[2] || '').replace('facturación del ', ''),
          suspension: (lines[3] || '').replace('Fecha de Suspensión : ', ''),
          fechaPago: fmtDate(fac.fechapago),
          vencimiento: fmtDate(fac.vencimiento),
          subtotal: parseFloat(fac.subtotal || '0').toFixed(2),
          iva: parseFloat(fac.impuesto || '0').toFixed(2),
          total: parseFloat(fac.total || '0').toFixed(2),
          saldo: parseFloat(fac.saldo || '0').toFixed(2),
          bancoEmisor: resData?.banco_emisor || '',
          numeroComprobante: resData?.numero_comprobante || '',
          montoDepositado: parseFloat(resData?.monto || '0').toFixed(2), // Monto real depositado
          fechaImpresion,
        };

        // Paso 3: Enviar a N8N para generar el SVG
        const resComp = await fetchConTimeout(process.env.REACT_APP_N8N_URL_COMPROBANTE!, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const compData = await resComp.json();

        if (compData?.base64) {
          setComprobanteBase64(compData.base64); // SVG en base64, listo para mostrar
        } else {
          setErrorComprobante('No se pudo generar el comprobante.');
        }
      } else {
        setErrorComprobante('No se encontró la factura.');
      }
    } catch (e: any) {
      const esTimeout = e?.message === 'TIMEOUT';
      setErrorComprobante(esTimeout
        ? '⏱️ El servidor tardó demasiado generando el comprobante.'
        : '❌ Error al generar el comprobante.');
    } finally {
      detenerCuenta(setCountdownComp, intervalCompRef);
      setLoadingDetalle(false);
    }
  };

  // ── Registro del pago ────────────────────────────────────────────────────────

  /**
   * Envía los datos del comprobante validado a N8N para registrar el pago
   * en el sistema Mikrowisp. Si el pago es exitoso, genera el comprobante visual.
   */
  const handlePagarAhora = async () => {
    if (!resultado) return;
    setEnviandoPago(true);
    setMensaje("");
    iniciarCuenta(setCountdown, intervalRef);

    // Incluimos la cédula del cliente para que N8N pueda identificarlo en Mikrowisp
    const dataParaEnviar = { ...resultado, cedula };

    try {
      const res = await fetchConTimeout(process.env.REACT_APP_N8N_URL_DEPOSITO_PAGAR!, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataParaEnviar),
      });
      const raw = await res.json();
      const json = Array.isArray(raw) ? raw[0] : raw; // La respuesta puede ser array o objeto

      if (json?.estado === "exito") {
        detenerCuenta(setCountdown, intervalRef);
        setPagoExitoso(true);
        setMensajeTipo("success");
        setErrorPago(false);
        setEnviandoPago(false);

        // Si la respuesta trae el ID de la factura, generamos el comprobante
        if (json?.facturas_id) {
          generarComprobante(json.facturas_id, resultado);
        }
      } else {
        setMensaje(json?.mensaje ?? "⚠️ No se pudo registrar el pago. Intenta con otro comprobante.");
        setMensajeTipo("danger");
        setErrorPago(true);
      }
    } catch (e: any) {
      const esTimeout = e?.message === 'TIMEOUT';
      setMensaje(esTimeout
        ? "⏱️ El servidor tardó demasiado. Intenta de nuevo."
        : "❌ Error al conectar con el servidor.");
      setMensajeTipo("danger");
      setErrorPago(true);
    } finally {
      detenerCuenta(setCountdown, intervalRef);
      setEnviandoPago(false);
    }
  };

  // ── Componente visual: cuenta regresiva circular ──────────────────────────────
  /**
   * Muestra un círculo con cuenta regresiva y una barra de progreso.
   * El círculo usa "conic-gradient" para crear el efecto de "reloj".
   */
  const CuentaRegresiva = ({ valor, label }: { valor: number; label: string }) => (
    <div className="text-center my-3">
      <p className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>{label}</p>
      <div style={{
        width: 64, height: 64, borderRadius: '50%', margin: '0 auto 8px',
        // conic-gradient crea el efecto de "arco" que va disminuyendo
        background: `conic-gradient(var(--sn-marca) ${(valor / TIMEOUT_SEG) * 360}deg, var(--sn-superficie-3) 0deg)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* Círculo interior con el número */}
        <div style={{
          width: 50, height: 50, borderRadius: '50%', background: 'var(--sn-superficie)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 18, fontWeight: 700, color: 'var(--sn-marca-texto)',
        }}>
          {valor}
        </div>
      </div>
      {/* Barra de progreso lineal */}
      <div style={{ height: 6, borderRadius: 4, background: 'var(--sn-superficie-3)', overflow: 'hidden', maxWidth: 200, margin: '0 auto' }}>
        <div style={{
          height: '100%', borderRadius: 4, background: 'var(--sn-marca)',
          width: `${(valor / TIMEOUT_SEG) * 100}%`,
          transition: 'width 0.9s linear', // Animación suave cada segundo
        }} />
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Subir Comprobante de Depósito</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Resumen del cliente en la parte superior */}
        <div className="alert alert-secondary py-2">
          <strong>Cliente:</strong> {clienteNombre} &nbsp;|&nbsp;
          <strong>Estado:</strong> {clienteEstado} &nbsp;|&nbsp;
          <strong>Cédula:</strong> {cedula}
        </div>

        {/* ── PANTALLA DE PAGO EXITOSO ── */}
        {pagoExitoso ? (
          showComprobante && comprobanteBase64 ? (
            // Si ya tenemos el comprobante y el usuario quiere verlo
            <ComprobanteTicket
              svgBase64={comprobanteBase64}
              facturaId={detalleFactura?.factura?.id || ''}
              onClose={() => setShowComprobante(false)}
            />
          ) : (
            // Pantalla de éxito con botón para ver el comprobante
            <div className="text-center p-3">
              <div style={{ fontSize: 72, lineHeight: 1 }}>✅</div>
              <h4 className="text-success mt-3 mb-1">¡Tu pago ha sido realizado con éxito!</h4>
              <p className="text-muted mt-2 mb-3" style={{ fontSize: '0.9rem' }}>
                Gracias por tu pago. El servicio será activado en breve.
              </p>

              {/* Muestra la cuenta regresiva mientras genera el comprobante */}
              {loadingDetalle && countdownComp !== null && (
                <CuentaRegresiva valor={countdownComp} label="Generando comprobante..." />
              )}
              {loadingDetalle && countdownComp === null && (
                <div className="mb-3"><Spinner animation="border" size="sm" className="me-2" />Generando comprobante...</div>
              )}

              {errorComprobante && (
                <Alert variant="warning" className="text-start">
                  {errorComprobante}
                  <div className="mt-2">
                    <Button size="sm" variant="outline-warning" onClick={() => {
                      setErrorComprobante('');
                      if (detalleFactura) generarComprobante(detalleFactura.factura.id, resultado);
                    }}>
                      🔄 Reintentar
                    </Button>
                  </div>
                </Alert>
              )}

              {/* El botón de comprobante se activa cuando el SVG está listo */}
              <Button
                variant="primary" size="lg" className="w-100 mb-2"
                disabled={loadingDetalle || (!comprobanteBase64 && !errorComprobante)}
                onClick={() => { if (comprobanteBase64) setShowComprobante(true); }}
              >
                {loadingDetalle ? 'Preparando...' : '🧾 Ver comprobante'}
              </Button>
              <Button variant="outline-secondary" className="w-100" onClick={handleClose}>
                Cerrar
              </Button>
            </div>
          )

        ) : step === 1 ? (
          /* ── PASO 1: Seleccionar imagen del comprobante ── */
          <>
            {/* Cuentas bancarias para hacer el depósito */}
            <div className="mb-3 p-3 rounded" style={{ background: 'var(--sn-info-suave)', border: '1px solid var(--sn-borde)' }}>
              <p className="mb-2" style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--sn-info-texto)' }}>
                🏦 Cuentas corrientes a nombre de SISNETEL FIBER EC (RUC: 1391931926001)
              </p>
              {[
                { banco: 'Banco Pichincha', cuenta: '2100 310 307' },
                { banco: 'Banco de Guayaquil', cuenta: '35 835 490' },
                { banco: 'Produbanco', cuenta: '2006191599' },
              ].map((b) => (
                <div key={b.banco} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', padding: '3px 0', color: 'var(--sn-info-texto)' }}>
                  <span>{b.banco}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <strong>{b.cuenta}</strong>
                    <button
                      type="button"
                      onClick={() => copiarCuenta(b.banco, b.cuenta)}
                      style={{
                        fontSize: '0.65rem', fontWeight: 700, color: 'var(--sn-info-texto)', background: 'var(--sn-superficie)',
                        border: '1px solid var(--sn-borde-fuerte)', borderRadius: 999, padding: '2px 7px', cursor: 'pointer',
                      }}
                    >
                      {cuentaCopiada === b.banco ? '✓' : '📋'}
                    </button>
                  </span>
                </div>
              ))}
              <p className="mb-0 mt-2" style={{ fontSize: '0.72rem', color: 'var(--sn-texto-2)' }}>
                Realiza el depósito y sube aquí la foto del comprobante.
              </p>
            </div>

            {/* Vista previa de la imagen seleccionada */}
            {previewUrl && !isPdf && (
              <div className="d-flex align-items-center gap-2 mb-3 p-2 rounded"
                style={{ background: "var(--sn-superficie-2)", border: "1px solid var(--sn-borde)" }}>
                <img src={previewUrl} alt="preview"
                  style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: "1px solid var(--sn-borde)", flexShrink: 0 }} />
                <span style={{ fontSize: "0.8rem", color: "var(--sn-texto-2)" }}>Vista previa seleccionada</span>
              </div>
            )}
            {isPdf && file && (
              <div className="mb-3 p-2 rounded"
                style={{ background: "var(--sn-alerta-suave)", border: "1px solid var(--sn-alerta)", fontSize: "0.85rem", color: "var(--sn-alerta-texto)" }}>
                📄 <strong>{file.name}</strong>
              </div>
            )}

            <p className="text-muted small mb-2">¿Cómo deseas subir el comprobante?</p>
            <div className="d-flex gap-2 mb-3 justify-content-center">
              {/* Botón de cámara */}
              <button onClick={handleTakePhoto} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                gap: "0.3rem", padding: "0.6rem 0.4rem", background: "var(--sn-marca-suave)",
                border: "2px solid var(--sn-marca)", borderRadius: 12, cursor: "pointer",
                fontSize: "0.78rem", color: "var(--sn-marca-texto)", fontWeight: 500,
              }}>
                <span style={{ fontSize: "1.8rem" }}>📷</span>
                <span>Tomar<br />foto</span>
              </button>
              {/* Botón de galería */}
              <button onClick={handleGallery} style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                gap: "0.3rem", padding: "0.6rem 0.4rem", background: "var(--sn-marca-suave)",
                border: "2px solid var(--sn-marca)", borderRadius: 12, cursor: "pointer",
                fontSize: "0.78rem", color: "var(--sn-marca-texto)", fontWeight: 500,
              }}>
                <span style={{ fontSize: "1.8rem" }}>🖼️</span>
                <span>Galería</span>
              </button>
            </div>

            {camError && <Alert variant="danger" className="py-2">{camError}</Alert>}
            {/* El botón Siguiente solo aparece si hay una imagen seleccionada */}
            {file && (
              <Button variant="primary" className="w-100" onClick={handleNext}>
                Siguiente ➡️
              </Button>
            )}
          </>

        ) : (
          /* ── PASO 2: Análisis con IA y confirmación de pago ── */
          <>
            {loading ? (
              // Mientras la IA analiza el comprobante
              <div className="text-center my-2">
                <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>Analizando comprobante con IA...</p>
                {countdown !== null
                  ? <CuentaRegresiva valor={countdown} label="Analizando comprobante con IA..." />
                  : <Spinner animation="border" />}
              </div>
            ) : (
              <>
                {mensaje && <Alert variant={mensajeTipo}>{mensaje}</Alert>}

                {/* Muestra los datos que la IA leyó del comprobante */}
                {resultado && (
                  <div className="card bg-light border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <h5 className="card-title">📄 Comprobante Analizado</h5>
                      <p className="mb-1"><strong>📆 Fecha:</strong> {resultado.fecha}</p>
                      <p className="mb-1"><strong>💰 Monto:</strong> ${resultado.monto}</p>
                      <p className="mb-1"><strong>🧾 Nº Comprobante:</strong> {resultado.numero_comprobante}</p>
                      <p className="mb-1"><strong>🏦 Banco Emisor:</strong> {resultado.banco_emisor}</p>
                      <p className="mb-1"><strong>👤 Destinatario:</strong> {resultado.nombre_destinatario}</p>
                      <p className="mb-1">
                        <strong>✅ Validación:</strong>{" "}
                        <span className={`badge bg-${resultado.validacion_cuenta === "validado" ? "success" : "danger"}`}>
                          {resultado.validacion_cuenta}
                        </span>
                      </p>
                      <p className="mb-0">
                        <strong>📎 Comprobante:</strong>{" "}
                        <a href={resultado.webViewLink} target="_blank" rel="noopener noreferrer">
                          Ver en Google Drive
                        </a>
                      </p>
                    </div>
                  </div>
                )}

                {/* Solo muestra el botón de pago si el comprobante es válido */}
                {validado && !errorPago && (
                  <div className="text-center mt-3">
                    {enviandoPago && countdown !== null
                      ? <CuentaRegresiva valor={countdown} label="Registrando pago..." />
                      : null}
                    <Button variant="success" size="lg" className="w-100" onClick={handlePagarAhora} disabled={enviandoPago}>
                      {enviandoPago
                        ? <><Spinner animation="border" size="sm" className="me-2" />Enviando...</>
                        : "✅ Pagar ahora"}
                    </Button>
                  </div>
                )}

                {/* Si hubo error, permite intentar con otra imagen */}
                {errorPago && (
                  <div className="text-center mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={() => {
                      setMensaje(""); setResultado(null); setValidado(false);
                      setErrorPago(false); setStep(1);
                    }}>
                      🔄 Intentar con otra imagen
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={enviandoPago}>
          {pagoExitoso ? "Cerrar" : "Cancelar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DepositoClienteModal;
